import { activityFromDecoded } from '@fit-editor/fit-core'
import type { Activity, FieldDefsByMesgNum } from '@fit-editor/fit-core'

import { decodeFitFileAsync } from '@/worker/fit-client'

export interface FileImportSuccess {
  readonly fileName: string
  readonly status: 'ok'
  readonly activity: Activity
  /** Captured at decode time -- see `activity-store.ts`'s `LoadedActivity.fieldDefsByMesgNum` for why this needs to travel alongside the `Activity`. */
  readonly fieldDefsByMesgNum: FieldDefsByMesgNum
}

export interface FileImportFailure {
  readonly fileName: string
  readonly status: 'failed'
  readonly message: string
}

export type FileImportOutcome = FileImportSuccess | FileImportFailure

/**
 * Reads a `File` as bytes via `FileReader` rather than `Blob.arrayBuffer()`
 * — both work in every real browser, but `FileReader` is also what jsdom
 * (used by the component tests) actually implements, so this keeps the
 * import path testable without reaching for a browser-only API jsdom
 * doesn't support.
 */
function readFileBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (result instanceof ArrayBuffer) resolve(new Uint8Array(result))
      else reject(new Error('Could not read file as binary data.'))
    }
    reader.onerror = () => reject(reader.error ?? new Error(`Could not read "${file.name}".`))
    reader.readAsArrayBuffer(file)
  })
}

/**
 * Decodes one file through the worker and builds its `Activity` domain
 * model. Per `fit-core`'s contract, `decodeFitFile` never throws — a
 * malformed file comes back as a successful `FileImportOutcome` whose
 * `activity.decodeErrors` is non-empty, which callers must surface
 * specifically (not a generic "failed to load"). This function's own
 * `'failed'` status is reserved for things outside `fit-core`'s contract
 * entirely: the file couldn't be read, or the worker call itself rejected.
 */
export async function importFitFile(file: File): Promise<FileImportOutcome> {
  try {
    const bytes = await readFileBytes(file)
    const decoded = await decodeFitFileAsync(bytes)
    const activity = activityFromDecoded(decoded)
    return {
      fileName: file.name,
      status: 'ok',
      activity,
      fieldDefsByMesgNum: decoded.fieldDefsByMesgNum,
    }
  } catch (error) {
    return {
      fileName: file.name,
      status: 'failed',
      message: error instanceof Error ? error.message : String(error),
    }
  }
}
